import { BrowserRouter, Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ThemedDialogsHost } from './components/ThemedDialogsHost';
import { WeatherOverlay } from './components/WeatherOverlay';
import AppLayout from './components/AppLayout';
import Dashboard from './pages/Dashboard';
import Modules from './pages/Modules';
import Tools from './pages/Tools';
import Wiki from './pages/Wiki';
import Logy from './pages/Logy';
import ModulePage from './pages/ModulePage';
import Api from './pages/Api';
import { lazy, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { mcpCall } from './lib/mcp';
import { LoadingStatus } from './components/ui';
import { IDENTITY_ME_QUERY_KEY, type IdentityMe } from './lib/identity-me';
import { UiAvailabilityGuard } from './components/UiAvailabilityGuard';

const MyPanel = lazy(() => import('./pages/MyPanel'));
const StartPage = lazy(() => import('./pages/StartPage'));
const MiniApps = lazy(() => import('./pages/MiniApps'));
const DesignSystem = lazy(() => import('./pages/DesignSystem'));
const Profile = lazy(() => import('./pages/Profile'));
const SandboxPage = lazy(() => import('./pages/SandboxPage'));
const BackupPage = lazy(() => import('./pages/modules/backup'));
const DockerPage = lazy(() => import('./pages/modules/docker'));
const MeshPage = lazy(() => import('./pages/modules/mesh'));
const NodeWorkspace = lazy(() => import('./pages/infra/NodeWorkspace'));
const PhoneWidgetPage = lazy(() => import('./pages/PhoneWidgetPage'));
const PhoneCodableProject = lazy(() => import('./pages/PhoneCodableProject'));
const PhoneCodable = lazy(() => import('./pages/PhoneCodable'));
const PhoneOrchestrator = lazy(() => import('./pages/PhoneOrchestrator'));
const PhoneWiki = lazy(() => import('./pages/PhoneWiki'));
const PhoneContainers = lazy(() => import('./pages/PhoneContainers'));
const PhoneProxy = lazy(() => import('./pages/PhoneProxy'));
const CodablePage = lazy(() => import('./pages/modules/codable'));
const CodableNew = lazy(() => import('./pages/modules/codable/CodableNew'));
const CodableLibrary = lazy(() => import('./pages/modules/codable/CodableLibrary'));
const CodableTemplates = lazy(() => import('./pages/modules/codable/CodableTemplates'));
const CodableWorkspace = lazy(() => import('./pages/modules/codable/CodableWorkspace'));
const MailPage = lazy(() => import('./pages/modules/mail'));

function EB({ children }: { children: React.ReactNode }) {
  return <ErrorBoundary>{children}</ErrorBoundary>;
}

function LegacyTierModuleRedirect() {
  const { name } = useParams();
  const [params] = useSearchParams();
  const q = params.toString();
  return <Navigate to={`/modules/${name ?? ''}${q ? `?${q}` : ''}`} replace />;
}

function RouteFallback({ status }: { status?: string } = {}) {
  const { t } = useTranslation();
  return (
    <LoadingStatus
      variant='page'
      title={t('common.loading', 'Loading…')}
      status={status ?? t('common.waitWorking', 'Working…')}
    />
  );
}

function HomeRedirect() {
  const { data, isLoading } = useQuery({
    queryKey: IDENTITY_ME_QUERY_KEY,
    queryFn: () => mcpCall<IdentityMe>('identity.me'),
    staleTime: 60_000,
    retry: false,
  });
  if (isLoading) return <RouteFallback />;
  const caps = data?.capabilities;
  const platform = caps?.platform === true || caps?.is_admin === true || caps?.all_modules === true;
  if (!platform && caps?.modules?.includes('codable')) {
    return <Navigate to='/modules/codable' replace />;
  }
  return <Navigate to='/core/dashboard' replace />;
}

function LazyRoute({ children, label }: { children: React.ReactNode; label?: string }) {
  return <Suspense fallback={<RouteFallback status={label} />}>{children}</Suspense>;
}

function SandboxCrash() {
  const { t } = useTranslation();
  return (
    <div className='flex flex-col items-center justify-center h-64 gap-4'>
      <div className='w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center'>
        <span className='text-2xl'>!</span>
      </div>
      <div className='text-center'>
        <h3 className='text-white font-semibold mb-1'>{t('errors.sandboxCrashTitle')}</h3>
        <p className='text-sm text-gray-500'>{t('errors.sandboxCrashBody')}</p>
      </div>
    </div>
  );
}

export default function App() {
  const { t } = useTranslation();
  return (
    <BrowserRouter future={{ v7_startTransition: true }}>
      <UiAvailabilityGuard />
      <div data-acm-app-content>
        <ThemedDialogsHost />
        <WeatherOverlay />
        <Routes>
        <Route path='/' element={<AppLayout />}>
          <Route index element={<HomeRedirect />} />
          {/* Start = servery + menu + moje plocha (bývalý Panel) */}
          <Route
            path='start'
            element={
              <EB>
                <LazyRoute label='Nacitam Start…'>
                  <StartPage />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='apps'
            element={
              <EB>
                <LazyRoute label='Nacitam aplikace…'>
                  <MiniApps />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='apps/:id'
            element={
              <EB>
                <LazyRoute label='Nacitam aplikace…'>
                  <MiniApps />
                </LazyRoute>
              </EB>
            }
          />
          <Route path='phone' element={<Navigate to='/start' replace />} />
          <Route path='help' element={<Navigate to='/core/wiki' replace />} />
          <Route
            path='phone/codable/:id'
            element={
              <EB>
                <LazyRoute label={t('common.nacitamProjekt', 'Nacitam projekt…')}>
                  <PhoneCodableProject />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='phone/codable'
            element={
              <EB>
                <LazyRoute label='Nacitam Codable…'>
                  <PhoneCodable />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='phone/orchestrator'
            element={
              <EB>
                <LazyRoute label='Loading orchestrator…'>
                  <PhoneOrchestrator />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='phone/wiki'
            element={
              <EB>
                <LazyRoute label='Nacitam wiki…'>
                  <PhoneWiki />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='phone/containers'
            element={
              <EB>
                <LazyRoute label='Nacitam kontejnery…'>
                  <PhoneContainers />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='phone/proxy'
            element={
              <EB>
                <LazyRoute label='Nacitam proxy…'>
                  <PhoneProxy />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='phone/:slug'
            element={
              <EB>
                <LazyRoute label='Nacitam…'>
                  <PhoneWidgetPage />
                </LazyRoute>
              </EB>
            }
          />

          {/* ── Core — platformní stránky ────────────────────────── */}
          <Route
            path='core/dashboard'
            element={
              <EB>
                <Dashboard />
              </EB>
            }
          />
          <Route
            path='core/panel'
            element={
              <EB>
                <LazyRoute label='Nacitam panel...'>
                  <MyPanel />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='core/containers'
            element={
              <EB>
                <LazyRoute label='Nacitam kontejnery...'>
                  <DockerPage />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='core/wiki'
            element={
              <EB>
                <Wiki />
              </EB>
            }
          />
          <Route
            path='core/logy'
            element={
              <EB>
                <Logy />
              </EB>
            }
          />
          <Route path='core/users' element={<Navigate to='/identity?type=user' replace />} />
          <Route path='core/store' element={<Navigate to='/modules' replace />} />
          <Route
            path='modules'
            element={
              <EB>
                <Modules />
              </EB>
            }
          />
          <Route
            path='core/design'
            element={
              <EB>
                <LazyRoute label='Nacitam Design System...'>
                  <DesignSystem />
                </LazyRoute>
              </EB>
            }
          />

          {/* Core moduly (toolbox, tasks) — ModulePage s fixnim jmenem */}
          <Route
            path='core/toolbox'
            element={
              <EB>
                <ModulePage coreName='toolbox' tier='core' />
              </EB>
            }
          />
          <Route
            path='core/tasks'
            element={
              <EB>
                <ModulePage coreName='tasks' tier='core' />
              </EB>
            }
          />
          {/* Infrastruktura = widget ModuleWorkspace (jako docker/containers), ne ModulePage chrome */}
          <Route
            path='core/mesh'
            element={
              <EB>
                <LazyRoute label='Nacitam infrastrukturu...'>
                  <MeshPage />
                </LazyRoute>
              </EB>
            }
          />
          {/* Infrastruktura nodu — dynamické záložky z module-ui-catalog + stejné widgety */}
          <Route
            path='infra/nodes/:nodeId'
            element={
              <EB>
                <LazyRoute label='Nacitam nod...'>
                  <NodeWorkspace />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='infra/nodes'
            element={<Navigate to='/infra/nodes/local' replace />}
          />
          <Route
            path='core/acm-link'
            element={
              <EB>
                <ModulePage coreName='acm-link' tier='core' />
              </EB>
            }
          />

          {/* ── Hardcoded utility stranky ─────────────────────────── */}
          <Route
            path='profile'
            element={
              <EB>
                <LazyRoute label='Nacitam profil...'>
                  <Profile />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='identity'
            element={
              <EB>
                <Api />
              </EB>
            }
          />
          <Route path='api' element={<Navigate to='/identity' replace />} />
          <Route
            path='tools'
            element={
              <EB>
                <Tools />
              </EB>
            }
          />

          {/* ── Core moduly — generická route (core:true v module.json) ──
              Statické core stránky výše (dashboard/panel/mesh/toolbox/…) mají
              přednost dle React Router rankingu; tahle chytá zbylé core moduly.
              Kontrakt UI identity 2026-07-24: jedna route pro core, jedna pro
              standardní; žádné dev/test štěpení dle maturity (byl to drift). */}
          <Route
            path='core/:name'
            element={
              <EB>
                <ModulePage tier='core' />
              </EB>
            }
          />

          {/* Staré maturity cesty /modules/test|dev/:name — dnes je jeden /modules/:name. */}
          <Route path='modules/test/:name' element={<LegacyTierModuleRedirect />} />
          <Route path='modules/dev/:name' element={<LegacyTierModuleRedirect />} />

          {/* ── Standardní moduly (core:false) ────────────────────── */}
          <Route
            path='modules/:name'
            element={
              <EB>
                <ModulePage tier='stable' />
              </EB>
            }
          />

          {/* Pošta — full-bleed workspace (ne ModulePage chrome) */}
          <Route
            path='mail'
            element={
              <EB>
                <LazyRoute>
                  <MailPage />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='modules/mail'
            element={
              <EB>
                <LazyRoute>
                  <MailPage />
                </LazyRoute>
              </EB>
            }
          />

          {/* ── Codable — ModuleWorkspace (projekty + settings, node switch) ── */}
          <Route
            path='modules/codable'
            element={
              <EB>
                <LazyRoute label='Nacitam Codable...'>
                  <CodablePage />
                </LazyRoute>
              </EB>
            }
          />
          {/* Statický segment 'settings' — tab v ModuleWorkspace (ne ModulePage). */}
          <Route
            path='modules/codable/settings'
            element={
              <EB>
                <LazyRoute label='Nacitam Codable...'>
                  <CodablePage />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='modules/codable/new'
            element={
              <EB>
                <LazyRoute label='Nacitam wizard...'>
                  <CodableNew />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='modules/codable/library'
            element={
              <EB>
                <LazyRoute label='Nacitam Codable knihovnu...'>
                  <CodableLibrary />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='modules/codable/templates'
            element={
              <EB>
                <LazyRoute label='Nacitam Codable sablony...'>
                  <CodableTemplates />
                </LazyRoute>
              </EB>
            }
          />
          <Route
            path='modules/codable/:id'
            element={
              <EB>
                <LazyRoute label='Nacitam workspace...'>
                  <CodableWorkspace />
                </LazyRoute>
              </EB>
            }
          />

          {/* ── Backup — kompletni backup UI ──────────────────────── */}
          <Route
            path='backup'
            element={
              <EB>
                <LazyRoute label='Nacitam backup...'>
                  <BackupPage />
                </LazyRoute>
              </EB>
            }
          />

          {/* ── Sandbox — izolovany, nemuze shodit UI ─────────────── */}
          <Route
            path='sandbox/:name'
            element={
              <ErrorBoundary fallback={<SandboxCrash />}>
                <LazyRoute label={t('common.nacitamSandboxModul', 'Nacitam sandbox modul...')}>
                  <SandboxPage />
                </LazyRoute>
              </ErrorBoundary>
            }
          />
        </Route>
        </Routes>
      </div>
    </BrowserRouter>
  );
}
